/**
 * The workspace's connected repositories, as `GET /api/repos` lists them.
 * One parse for every reader (the Agent runs skills list, a Team's Works on
 * switches, the Team repo picker), so the id/owner/name validation never
 * drifts between them.
 */
export interface WorkspaceRepo {
  id: string;
  /** `owner/name`. */
  fullName: string;
  /** What the repository is for, from its index (YAG-831). Null until indexed. */
  description: string | null;
  /** Its primary language, from its index. Null until indexed. */
  language: string | null;
}

/** The rows of a `/api/repos` body that carry an id, owner and name, sorted by name. */
export function parseWorkspaceRepos(data: unknown): WorkspaceRepo[] {
  if (!Array.isArray(data)) return [];
  const out: WorkspaceRepo[] = [];
  for (const repo of data as Array<{
    id?: unknown;
    owner?: unknown;
    name?: unknown;
    description?: unknown;
    language?: unknown;
  }>) {
    if (
      typeof repo.id !== "string" ||
      typeof repo.owner !== "string" ||
      typeof repo.name !== "string"
    ) {
      continue;
    }
    if (out.some((entry) => entry.id === repo.id)) continue;
    out.push({
      id: repo.id,
      fullName: `${repo.owner}/${repo.name}`,
      description:
        typeof repo.description === "string" && repo.description.trim() ? repo.description : null,
      language: typeof repo.language === "string" && repo.language.trim() ? repo.language : null,
    });
  }
  return out.sort((a, b) => a.fullName.localeCompare(b.fullName));
}

export class WorkspaceReposError extends Error {
  constructor(readonly status: number) {
    super(`GET /api/repos answered ${status}`);
  }
}

/** Reads the workspace's repositories; throws `WorkspaceReposError` with the status on a non-OK answer. */
export async function fetchWorkspaceRepos(): Promise<WorkspaceRepo[]> {
  const res = await fetch("/api/repos", { credentials: "include" });
  if (!res.ok) throw new WorkspaceReposError(res.status);
  return parseWorkspaceRepos(await res.json());
}
